<template>
  <div class="step2-container">
    <div style="margin-bottom: 10px" v-if="!formData.experimentStatus">
      <Button @click="$emit('back')" type="error">返回上一步</Button>
    </div>
    <h2 style="margin: 20px 0">具体方案设置</h2>

    <div style="display: flex; align-items: center; gap: 20px">
      <span style="width: 120px; text-align: right">实验组数量：</span>
      <RadioGroup
        v-model="experimentGroupCount"
        type="button"
        @on-change="handleGroupCountChange($event, true)"
        button-style="solid"
      >
        <Radio :disabled="!!formData.experimentStatus" :label="1">1</Radio>
        <Radio :disabled="!!formData.experimentStatus" :label="2">2</Radio>
        <Radio :disabled="!!formData.experimentStatus" :label="3">3</Radio>
        <Radio :disabled="!!formData.experimentStatus" :label="4">4</Radio>
      </RadioGroup>
    </div>

    <ExperimentCapabilityConfig
      ref="capabilityConfigRef"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="!!formData.experimentStatus"
    />

    <FormItem label="流量分配规则：">
      <TrafficDistribution
        :disabled="!!formData.experimentStatus"
        :value="groups.map((group) => group.participateRatio)"
        :groupNames="
          groups.map((group, index) => (index ? '实验组' + index : '对照组'))
        "
        @change="
          groups = $event.map((percent, index) => {
            return { ...groups[index], participateRatio: percent.percent }
          })
        "
      />
    </FormItem>

    <FormItem
      label="最大样本量："
      prop="experimentUserNum"
      :rules="[
        {
          required: true,
          validator(rule, value, callback) {
            if (!value) {
              callback('请输入最大样本量')
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ]"
    >
      <InputNumber
        :precision="0"
        :min="0"
        :max="1000000"
        style="width: 300px"
        :disabled="!!formData.experimentStatus"
        placeholder="最大样本量"
        v-model="formData.experimentUserNum"
      />
    </FormItem>

    <FormItem
      label="实验名称："
      prop="experimentName"
      :rules="[{ required: true, message: '请输入实验名称', trigger: 'blur' }]"
    >
      <Input
        style="width: 300px"
        :disabled="mode === 'view'"
        placeholder="请输入"
        v-model="formData.experimentName"
        maxlength="50"
        show-word-limit
      />
    </FormItem>
  </div>
</template>

<script>
import {
  createGroupParamByCapability,
  normalizeGroupParamByCapability
} from '../helpers'
import ExperimentCapabilityConfig from './ExperimentCapabilityConfig.vue'
import TrafficDistribution from './TrafficDistribution.vue'

export default {
  name: 'Step2',
  components: {
    ExperimentCapabilityConfig,
    TrafficDistribution
  },
  props: {
    mode: {
      type: String,
      default: 'create'
    },
    formData: {
      type: Object,
      default: () => ({
        maxSampleSize: '',
        experimentName: ''
      })
    }
  },
  data() {
    const initialGroupCount =
      Array.isArray(this.formData.groups) && this.formData.groups.length
        ? this.formData.groups.length
        : 1

    return {
      experimentGroupCount: initialGroupCount,
      groups: []
    }
  },
  created() {
    this.warmupFormStructure()
  },
  mounted() {
    this.handleGroupCountChange(
      this.formData.groups.length || this.experimentGroupCount || 1,
      !this.formData.groups.some((item) => item.participateRatio)
    )
  },
  methods: {
    getCapabilityConfigRef() {
      return this.$refs.capabilityConfigRef
    },
    getDefaultGroupParam() {
      return createGroupParamByCapability(this.formData.experimentCapability)
    },
    normalizeGroupParam(param) {
      return normalizeGroupParamByCapability(
        this.formData.experimentCapability,
        param
      )
    },
    ensureGroupsArray() {
      if (!Array.isArray(this.formData.groups)) {
        this.$set(this.formData, 'groups', [])
      }
    },
    shouldInitControlGroupParam() {
      const controlGroupParam = this.formData.controlGroupParam
      return (
        controlGroupParam === undefined ||
        controlGroupParam === null ||
        !Object.keys(controlGroupParam).length
      )
    },
    warmupFormStructure() {
      this.ensureGroupsArray()

      if (this.shouldInitControlGroupParam()) {
        this.$set(this.formData, 'controlGroupParam', this.getDefaultGroupParam())
      } else {
        this.$set(
          this.formData,
          'controlGroupParam',
          this.normalizeGroupParam(this.formData.controlGroupParam)
        )
      }

      const nextGroups = Array.from(
        { length: this.experimentGroupCount },
        (_, index) => {
          const group = this.formData.groups[index] || {}
          return {
            ...group,
            experimentGroupName: group.experimentGroupName || '实验组' + (index + 1),
            experimentGroupParam: this.normalizeGroupParam(
              group.experimentGroupParam
            )
          }
        }
      )

      this.$set(this.formData, 'groups', nextGroups)
    },
    createGroupParam() {
      const target = this.getCapabilityConfigRef()
      return target && target.createGroupParam
        ? target.createGroupParam()
        : this.getDefaultGroupParam()
    },
    syncCapabilityGroupParams() {
      const target = this.getCapabilityConfigRef()
      if (target && target.syncGroupParams) {
        target.syncGroupParams()
      }
    },
    handleGroupCountChange(count, useEqualRatio) {
      this.experimentGroupCount = count

      if (this.formData.groups.length < count) {
        for (let i = this.formData.groups.length; i < count; i++) {
          this.formData.groups.push({
            experimentGroupName: '实验组' + (i + 1),
            experimentGroupParam: this.createGroupParam()
          })
        }
      } else if (this.formData.groups.length > count) {
        this.formData.groups = this.formData.groups.slice(0, count)
      }

      this.syncCapabilityGroupParams()

      if (this.formData.id && !useEqualRatio) {
        const controlRatio =
          this.formData.controlGroupParticipateRatio !== undefined &&
          this.formData.controlGroupParticipateRatio !== null
            ? Number(this.formData.controlGroupParticipateRatio)
            : 100 -
              this.formData.groups.reduce(
                (acc, cur) => acc + Number(cur.participateRatio || 0),
                0
              )
        this.groups = [
          {
            experimentGroupName: this.formData.controlGroupName || '对照组',
            participateRatio: controlRatio
          },
          ...this.formData.groups.map((item) => ({
            ...item
          }))
        ]
        return
      }

      this.groups = [
        {
          experimentGroupName: '对照组',
          participateRatio: 0
        },
        ...this.formData.groups.map((item) => ({
          ...item,
          participateRatio: 0
        }))
      ]

      let remainingPercent = 100
      for (let i = 0; i < this.groups.length; i++) {
        if (i === this.groups.length - 1) {
          this.groups[i].participateRatio = remainingPercent
          continue
        }
        const percent = Math.floor(100 / this.groups.length)
        this.groups[i].participateRatio = percent
        remainingPercent -= percent
      }
    },
    handleGetDefaultGroup() {
      const target = this.getCapabilityConfigRef()
      if (target && target.handleGetDefaultGroup) {
        return target.handleGetDefaultGroup()
      }
      return Promise.resolve()
    },
    applyDefaultGroup(defaultParam) {
      const target = this.getCapabilityConfigRef()
      if (target && target.applyDefaultGroup) {
        return target.applyDefaultGroup(defaultParam)
      }
      return Promise.resolve()
    },
    validateFeatureConfig() {
      const target = this.getCapabilityConfigRef()
      if (!target || !target.validate) return true
      return target.validate()
    },
    getFormData() {
      return {
        experimentGroupCount: this.experimentGroupCount,
        groups: this.formData.groups,
        controlGroupParam: this.formData.controlGroupParam,
        trafficDistribution: this.groups.map((group) => group.participateRatio),
        maxSampleSize: this.formData.maxSampleSize,
        experimentName: this.formData.experimentName
      }
    },
    resetForm() {
      this.experimentGroupCount = 1
      this.formData.groups = []
      const target = this.getCapabilityConfigRef()
      if (target && target.resetControlGroupParam) {
        target.resetControlGroupParam()
      }
    }
  }
}
</script>

<style scoped lang="less">
.step2-container {
  padding: 20px;
  padding-top: 0;
}
</style>
